
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders } from '@/lib/api-helper';

interface OnlineUser {
  userId: number;
  username: string;
  isOnline: boolean;
  lastSeen: string | null;
}

/**
 * Hook per gestire la presenza online dell'utente corrente
 */
export const usePresence = () => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();

  // Invia heartbeat ogni 2 minuti
  const sendHeartbeat = async () => {
    try {
      const response = await fetch('/api/presence/heartbeat', {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        console.log('[PRESENCE] Heartbeat inviato con successo');
      }
    } catch (error) {
      console.error('[PRESENCE] Errore invio heartbeat:', error);
    }
  };

  // Imposta utente offline
  const setOffline = async () => {
    try {
      await fetch('/api/presence/offline', {
        method: 'POST',
        headers: getAuthHeaders()
      });
      console.log('[PRESENCE] Stato offline impostato');
    } catch (error) {
      console.error('[PRESENCE] Errore impostazione offline:', error);
    }
  };

  useEffect(() => {
    // Invia heartbeat immediato
    sendHeartbeat();

    // Heartbeat ogni 90 secondi (1.5 minuti) per stato più accurato
    intervalRef.current = setInterval(sendHeartbeat, 90 * 1000);

    // Gestisci eventi di chiusura pagina
    const handleBeforeUnload = () => {
      setOffline();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setOffline();
      } else {
        sendHeartbeat();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setOffline();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return { sendHeartbeat, setOffline };
};

/**
 * Hook per ottenere lo stato online di tutti gli utenti (WebSocket real-time)
 */
export const useOnlineUsers = () => {
  const queryClient = useQueryClient();
  
  // Sistema semplificato: SOLO polling HTTP ogni 60 secondi
  return useQuery<{ onlineUsers: OnlineUser[] }>({
    queryKey: ['onlineUsers'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/presence/online-users', {
          headers: getAuthHeaders()
        });
        
        if (!response.ok) {
          throw new Error(`Errore nel recupero utenti online: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data || !Array.isArray(data.onlineUsers)) {
          console.warn('[PRESENCE] Struttura dati non valida:', data);
          return { onlineUsers: [] };
        }
        
        return data;
      } catch (error) {
        console.error('[PRESENCE] Errore recupero utenti online:', error);
        return { onlineUsers: [] };
      }
    },
    refetchInterval: 60000, // Polling ogni 60 secondi
    staleTime: 50000,
    retry: 2,
    retryDelay: 1000,
  });
};

/**
 * Hook per verificare se un utente specifico è online
 */
export const useIsUserOnline = (userId: number): boolean => {
  const { data, isLoading, error } = useOnlineUsers();
  
  // Se stiamo caricando o c'è un errore, ritorna false
  if (isLoading || error || !data) return false;
  
  // Controllo aggiuntivo per sicurezza
  if (!data.onlineUsers || !Array.isArray(data.onlineUsers)) return false;
  
  const user = data.onlineUsers.find(u => u.userId === userId);
  return user?.isOnline || false;
};
